// CHAT-DM (2026-10-28): click a name to message them, Slack-simple. A person's or DE's name or
// avatar anywhere in chat (message authors, the thread panel, the member list, @mentions) opens a
// small profile popover: name, role or seat, the DE/AG label, and "Message", which opens the DM
// with them (or creates it) and goes there. No space or channel in common is needed: any two
// members of the company can DM (`dms.open`). Not shown for the viewer themselves.
// CHAT-SWITCH-B: a person's card also shows their status, Active / Away, do-not-disturb and
// their local time (CHAT-SWITCH-A's statuses).

import { ApiError } from "@companyos/sdk";
import {
  AvatarTile,
  Button,
  Popover,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { MessageCircleIcon } from "lucide-react";
import { type ComponentProps, type ReactElement, type ReactNode, useId } from "react";
import { keepFocus } from "../../focus/focus";
import { reachRefusalToast } from "../agents/ask-access";
import {
  HelperDot,
  HelpersInfoTip,
  helperCounts,
  helpersText,
  helperWorking,
  useHelpers,
} from "../agents/helpers";
import { deLabel } from "../de/de-name";
import type { ChatMember } from "./chat-model";
import { chatKeys } from "./chat-queries";
import { MemberPresenceInfo } from "./member-status";
import type { ChatContext } from "./use-chat";

/** Opens (or reopens) the DM with `ids` and navigates to it; `onDone` runs before it goes. */
export function useOpenDm(chat: ChatContext, onDone?: () => void) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (ids: readonly string[]) =>
      chat.api.client.dms.open({ params: { co: chat.co }, body: { membership_ids: [...ids] } }),
    onSuccess: async (c) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: chatKeys.channels(chat.co) }),
        queryClient.invalidateQueries({ queryKey: chatKeys.prefs(chat.co) }),
      ]);
      onDone?.();
      void navigate({
        to: "/c/$slug/chat/$channelId",
        params: { slug: chat.company.slug, channelId: c.id },
        search: keepFocus as never,
      });
    },
    // A client viewer without a seat (a guest) opens no DMs; the client cannot tell a seated
    // client viewer (who may) from one, so the server decides and the refusal says why.
    onError: (e) => {
      // REACH-S1: an agent outside whose Hears from the viewer is — the toast offers "Ask for access".
      if (reachRefusalToast(chat.api, chat.co, e)) return;
      toast.add({
        title:
          e instanceof ApiError && e.status === 403
            ? "Guests can't start direct messages."
            : "Could not open the message.",
        type: "error",
      });
    },
  });
}

/** Whether the viewer can start a DM: a membership here and chat writable (as the sidebar's "+"). */
export const canMessage = (chat: ChatContext) => chat.meId !== null && !chat.readOnly;

/**
 * The profile popover of `memberId` around `trigger` (a button). An id the member index does
 * not know (a former member, agency staff) shows `fallback` instead. `ag` marks a crew agent
 * (AG), which has no DM of its own.
 */
export function MemberPopover({
  chat,
  memberId,
  trigger,
  ag,
  fallback,
}: {
  chat: ChatContext;
  memberId: string;
  trigger: ReactElement;
  ag?: { name: string; owner?: string | undefined } | undefined;
  /** What an unknown member shows instead of the (then useless) trigger. */
  fallback?: ReactNode;
}): ReactNode {
  const who = chat.members.get(memberId);
  if (!who && !ag) return fallback ?? null;
  const de = who?.kind === "de";
  // HELPERS-2 (Codex HELPERS): a DE's person name, when the company names DEs by person.
  const name = ag
    ? ag.name
    : de
      ? deLabel(workerName(who?.name ?? ""), who?.personName)
      : (who?.name ?? "");
  const me = !ag && memberId === chat.meId;
  const detail = ag
    ? ag.owner
      ? `${ag.owner}'s agent`
      : "Agent"
    : de
      ? (who?.seat?.replace(/\s*\(DE\)$/, "") ?? "Digital employee")
      : (who?.role ?? "Person");
  return (
    <Popover>
      <PopoverTrigger render={trigger} />
      <PopoverContent
        align="start"
        className="w-64 gap-3 p-3"
        data-slot="member-card"
        // A click in the (portalled) card must not reach the message row (it opens the thread).
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5">
          <AvatarTile
            name={name}
            kind={de || ag ? "agent" : "person"}
            size="lg"
            aria-hidden="true"
          />
          <div className="flex min-w-0 flex-col">
            <PopoverTitle className="flex items-center gap-1.5 truncate text-sm">
              <span className="truncate">{name}</span>
              {de ? <WorkerBadge kind="de" size="sm" /> : null}
              {ag ? <WorkerBadge kind="ag" size="sm" /> : null}
            </PopoverTitle>
            <span className="truncate text-xs text-muted-foreground" data-slot="member-card-detail">
              {me ? `${detail} · You` : detail}
            </span>
          </div>
        </div>
        {/* CHAT-SWITCH-B: a person's status, Active / Away, do-not-disturb and local time. */}
        {!de && !ag ? <MemberPresenceInfo status={chat.statuses?.get(memberId)} /> : null}
        {/* HELPERS-2: "14 helpers · 3 working" (nothing at zero); opens the DE's Helpers tab. */}
        {de && chat.api ? <DeHelpersLine chat={chat} deId={memberId} /> : null}
        {/* HELPERS-2 (design §4): the helpers a person leads, on their profile. */}
        {!de && !ag && who && chat.api ? <PersonHelpers chat={chat} personId={memberId} /> : null}
        {!me && !ag && who && canMessage(chat) ? (
          <MessageButton chat={chat} memberId={memberId} name={name} blocked={dmBlockedBy(who)} />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

/** HELPERS-2: a DE card's helpers line (mounted with the open card only). Nothing at zero. */
function DeHelpersLine({ chat, deId }: { chat: ChatContext; deId: string }) {
  const h = useHelpers(chat.api, chat.co);
  const { total, working } = helperCounts(h.byLead.get(deId), h.now);
  if (!total) return null;
  return (
    <Link
      to="/c/$slug/agents/de/$id"
      params={{ slug: chat.company.slug, id: deId }}
      search={{ ...(keepFocus({}) as object), tab: "helpers" } as never}
      className="self-start text-xs text-muted-foreground hover:text-foreground hover:underline"
      data-slot="member-card-helpers"
    >
      {helpersText(total, working)}
    </Link>
  );
}

/**
 * HELPERS-2 (Codex HELPERS): a person's profile lists the helpers they lead directly (each an AG
 * with its working dot, opening its own page). Nothing when they lead none.
 */
function PersonHelpers({ chat, personId }: { chat: ChatContext; personId: string }) {
  const h = useHelpers(chat.api, chat.co);
  const list = h.byLead.get(personId) ?? [];
  if (!list.length) return null;
  const { total, working } = helperCounts(list, h.now);
  return (
    <div className="flex min-w-0 flex-col gap-1" data-slot="member-card-person-helpers">
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        {helpersText(total, working)}
        <HelpersInfoTip />
      </span>
      <ul className="flex min-w-0 flex-col gap-0.5">
        {list.map((a) => (
          <li key={a.id} className="flex min-w-0 items-center gap-1.5" data-helper={a.id}>
            <WorkerBadge kind="ag" size="sm" />
            <Link
              to="/c/$slug/agents/ag/$id"
              params={{ slug: chat.company.slug, id: a.id }}
              search={keepFocus}
              className="min-w-0 truncate text-xs text-foreground hover:underline"
            >
              {workerName(a.name)}
            </Link>
            <HelperDot working={helperWorking(a, h.now)} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Why no DM can open with `m` now, or null: the API opens DMs with active memberships only, so a
 * paused or deactivated person and a paused or retired DE are refused (existing DMs included).
 */
export function dmBlockedBy(m: ChatMember): string | null {
  if (m.kind === "de") {
    if (m.deStatus === "paused") return "Paused";
    if (m.deStatus === "not_deployed") return "Not deployed";
    if (m.deStatus === "retired") return "Retired";
    return null;
  }
  return m.inactive ? "Inactive" : null;
}

/** "Message": opens the DM; shown disabled, with the reason beside it, when `blocked`. */
export function MessageButton({
  chat,
  memberId,
  name,
  blocked,
  size = "sm",
  variant = "default",
}: {
  chat: ChatContext;
  memberId: string;
  name: string;
  blocked: string | null;
  size?: "sm" | "xs";
  variant?: "default" | "outline";
}) {
  const open = useOpenDm(chat);
  const reasonId = useId();
  return (
    <span className="inline-flex items-center gap-2">
      <Button
        type="button"
        size={size}
        variant={variant}
        aria-label={`Message ${name}`}
        disabled={blocked !== null || open.isPending}
        {...(blocked ? { "aria-describedby": reasonId } : {})}
        onClick={() => open.mutate([memberId])}
      >
        <MessageCircleIcon data-icon="inline-start" />
        Message
      </Button>
      {blocked ? (
        <span id={reasonId} className="text-xs text-muted-foreground" data-slot="dm-blocked">
          {blocked}: no new messages
        </span>
      ) : null}
    </span>
  );
}

/**
 * A name or avatar as the popover's trigger: a plain button that keeps the text's look. The
 * popover's trigger props (handlers, ARIA, ref) pass through to the button.
 */
export function MemberTrigger({
  label,
  children,
  className,
  ...props
}: Omit<ComponentProps<"button">, "type"> & { label?: string }) {
  return (
    <button
      {...props}
      type="button"
      data-slot="member-trigger"
      {...(label ? { "aria-label": label } : {})}
      className={
        className ??
        "cursor-pointer rounded-sm border-0 bg-transparent p-0 text-left text-inherit hover:underline focus-visible:outline-2 focus-visible:outline-ring"
      }
    >
      {children}
    </button>
  );
}
